import { ApiError, apiErrorCode } from "@/lib/api";
import {
  hasCapability,
  type AuthedRequester,
  type BrokerIdentity,
} from "@/lib/brokerAuth";
import { messageFor } from "@/lib/errors";
import {
  readActionStanding,
  type ActionStanding,
  type ActionStatus,
  type SignalRequestRefusal,
} from "@/lib/submissions/actionItems";
import type { BrokerQuote } from "@/lib/submissions/brokerSubmissions";
import { KEY_REUSED_MESSAGE } from "@/lib/submissions/quoteBinding";
import type { QuoteCard } from "@/lib/submissions/quoteCards";

// BRK-S463 — a referred quote's underwriter review, asked from the portal
// (PMS-S871, PMS-E044). A carrier that refers its price, Great American in
// UAT, answers through its own underwriter by email or phone. So the broker
// asks Signal from the quote: PMS opens a `referral_review` action for
// Signal's underwriters, who take it to the carrier's underwriter and record
// the decision (PMS-S749). An approval's conditions become subjectivities,
// and an approved indication a bindable quote (PMS-S750).

/** The quote's open review, else its latest, whoever opened it (PMS-S871). */
export type ReviewRequest = ActionStanding;

/** The longest note PMS takes with a review request. */
export const REVIEW_NOTE_MAX = 2000;

/** The quote header's button and its dialog's title, each word capitalized
 *  (Nav, BRK-S465). */
export const REVIEW_ACTION_LABEL = "Request Underwriter Review";

/** The card's chips while a review is open, by where it stands. Signal may
 *  open one itself when it sends a referral (PMS-S870), so none says who
 *  asked. */
export const REVIEW_REQUESTED_LABEL = "Review requested";
export const REVIEW_LABELS: readonly string[] = [
  REVIEW_REQUESTED_LABEL,
  "With the underwriter",
  "Needs your reply",
];

/** An open review's chip, by where it stands. */
export function reviewChip(status: ActionStatus): string {
  switch (status) {
    case "waiting_on_carrier":
      return "With the underwriter";
    case "waiting_on_broker":
      return "Needs your reply";
    default:
      return REVIEW_REQUESTED_LABEL;
  }
}

/** An open review's line, by where it stands. */
export function reviewLine(status: ActionStatus, carrier: string): string {
  switch (status) {
    case "waiting_on_carrier":
      return `Signal has sent it to ${carrier}’s underwriter for review.`;
    case "waiting_on_broker":
      return `Signal needs more information from you for ${carrier}’s underwriter, and will be in touch.`;
    default:
      return reviewRequestedLine(carrier);
  }
}

/** Whether a review is still being worked: not done, not closed. */
export function isOpenReview(
  review: ReviewRequest | null | undefined,
): boolean {
  return !!review && review.status !== "done" && review.status !== "cancelled";
}

/** Whether the broker may ask for the underwriter's review: a current
 *  referral that no one has taken to the underwriter, nor approved, with no
 *  review already open, for a broker with `submission:edit`. A declined
 *  referral may be asked again, with more to go on. */
export function canRequestReview(
  quote: BrokerQuote,
  identity: BrokerIdentity | null,
): boolean {
  return (
    quote.bindStatus === "refer" &&
    !quote.outdated &&
    quote.referral?.status !== "requested" &&
    quote.referral?.status !== "approved" &&
    !isOpenReview(quote.reviewRequest) &&
    (quote.stage === undefined ||
      quote.stage === "offered" ||
      quote.stage === "selected") &&
    hasCapability(identity, "submission:edit")
  );
}

/** Whether the quote's popup offers the review: where the broker can ask,
 *  and not on an expired price, which is priced again first (BRK-S462). */
export function canAskForReview(
  card: Pick<QuoteCard, "quote" | "expired">,
  identity: BrokerIdentity | null,
): boolean {
  return (
    !!card.quote && !card.expired && canRequestReview(card.quote, identity)
  );
}

/** What happens once the broker asked, in a line. */
export function reviewRequestedLine(carrier: string): string {
  return `Signal will take it to ${carrier}’s underwriter and let you know here.`;
}

/** The referral's next step while the broker can ask: the footer's primary
 *  (BRK-S468), as well as the header's Actions menu (BRK-S465). */
export function reviewNextLine(carrier: string): string {
  return `Choose ${REVIEW_ACTION_LABEL} below, and Signal takes it to ${carrier}’s underwriter.`;
}

const BASE = "/api/broker/submissions";

/** POST …/quotes/{quoteId}/review-request with the attempt's
 *  `Idempotency-Key`: the same key answers the item it opened, so a retry
 *  never asks twice. A blank note is left out. */
export async function requestUnderwriterReview(
  request: AuthedRequester,
  submissionId: string,
  quoteId: string,
  input: { idempotencyKey: string; note?: string },
): Promise<ReviewRequest> {
  const note = input.note?.trim();
  const answer = await request<unknown>(
    `${BASE}/${encodeURIComponent(submissionId)}/quotes/${encodeURIComponent(quoteId)}/review-request`,
    {
      method: "POST",
      body: note ? { note } : {},
      headers: { "Idempotency-Key": input.idempotencyKey },
    },
  );
  const review = readActionStanding(answer);
  if (!review) {
    throw new ApiError(
      200,
      "Signal answered, but not with the review. Reload to check.",
    );
  }
  return review;
}

/** A refused review request (`SignalRequestRefusal`). */
export type ReviewRefusal = SignalRequestRefusal;

export function reviewRefusalOf(err: unknown): ReviewRefusal {
  if (err instanceof ApiError && err.status === 0) {
    return {
      kind: "network",
      message:
        "We couldn’t confirm your request. Check the quote before trying again.",
    };
  }
  switch (apiErrorCode(err)) {
    case "review_in_progress":
      return {
        kind: "settled",
        message: "A review of this quote is already with Signal.",
      };
    case "not_referred":
      return {
        kind: "settled",
        message: "This quote no longer needs the underwriter’s review.",
      };
    case "quote_closed":
      return {
        kind: "settled",
        message:
          "This quote has expired or been withdrawn, so it can’t go for review.",
      };
    // PMS's sentence is about our key, not the broker's request.
    case "idempotency_key_reused":
      return { kind: "key_reused", message: KEY_REUSED_MESSAGE };
    default:
      return { kind: "refused", message: messageFor(err) };
  }
}
